import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiSend } from '../utils/api';

// Lightweight first-login walkthrough. Shows once per user (user.onboarded === false from
// /api/auth/me); dismissal POSTs /api/auth/onboarded and refreshes auth so it never reshows.
// Account-type aware: demo users are oriented around the sample data + what's locked; real new
// clients toward connecting their MLS and adding buyers. Not a tour engine — a few steps, skippable.
//
// ── The third variant: a guided tour for a simulated-outbound workspace ──────
// An org with org.simulated_outbound (migration 088) is a real, fully-featured workspace on real
// market data whose sends are simulated — the shape used for an evaluation account. That audience
// needs something the other two variants deliberately are not: a tour of the WHOLE product, one tab
// at a time, that is explicit about which numbers are live and which are staged.
//
// It is a DOCKED CORNER CARD, not a modal. Every other variant blanks the screen behind a card,
// which is right for three sentences and wrong for a tour — the entire point is that you are looking
// at the page being described. It navigates the router as you step and leaves the app fully usable;
// you can break off, click into a deal, and come back.
//
// Bottom-right, and narrow. The first cut spanned the full width along the bottom and swallowed the
// lower third of every page, which defeated the object. A corner is the convention for this kind of
// panel for a good reason, and this corner specifically: the top of a page carries its heading, its
// KPIs and its primary controls — exactly what several steps are pointing at — so anchoring over
// them would hide the thing being described. It also collapses to a pill, because no fixed position
// is right for every page and the reader should not have to put up with one that is briefly wrong.
//
// Step order follows the DEAL LIFECYCLE rather than the sidebar, because the sidebar order is
// alphabet-soup to someone who has never seen the product: land (Dashboard) → find (MLS, Off Market)
// → work (Deals) → measure (Pipeline) → sell (Buyers) → talk (Communications) → configure (Settings).

const DEMO_STEPS = [
  { title: 'Welcome to your feebe demo', body: 'You\'re in a live sandbox preloaded with sample Austin deals, MLS opportunities, and buyers — so you can try the whole workflow without touching anything real.' },
  { title: 'Explore the full pipeline', body: 'Browse MLS Opportunities, underwrite a deal with AI, generate a TREC contract, and see how buyer matching works. Everything runs on your isolated sample data.' },
  { title: 'Nothing real is sent', body: 'Emails, texts, and e-signatures are simulated in demo mode, and a few premium tools (like the Buyers Database) show an upgrade prompt. That\'s expected — upgrade any time to run real deals.' },
];

const REAL_STEPS = [
  { title: 'Welcome to feebe', body: 'Let\'s get you sourcing. Three quick things and you\'re off.' },
  { title: 'Connect your MLS market', body: 'Open MLS Opportunities to connect your market. Once connected, feebe scans it and surfaces scored Hot / Watch / Maybe opportunities — isolated to your organization.', cta: { label: 'Go to MLS Opportunities', to: '/mls-opportunities' } },
  { title: 'Build your buyers list', body: 'Add buyers with their buy-boxes so new deals auto-match to the right investors and your blasts go to the right people.', cta: { label: 'Go to Buyers', to: '/buyers' } },
];

// ── Guided tour ───────────────────────────────────────────────────────────────
// `live` / `staged` are rendered as labelled chips under each step. Keeping them as structured data
// rather than prose in the body is deliberate: the honesty of this tour is the product's credibility,
// so "what is real here" must be impossible to skim past, and must be written per screen rather than
// as one disclaimer at the start that nobody remembers by step seven.
const TOUR_STEPS = [
  {
    to: '/',
    tab: 'Getting started',
    nav: null,
    title: 'Welcome to feebe',
    body: 'feebe runs a wholesaling desk end to end: source from the MLS, underwrite with AI, paper the contract, market to buyers, and track every conversation. This tour walks the whole thing, one tab at a time. It takes about three minutes, and you can leave it at any point — click anywhere to look around and come back.',
    live: ['Live Phoenix MLS listings and sold comps', 'AI underwriting, comps and ARV', 'Contract generation'],
    staged: ['Outbound email, SMS and e-signature are simulated', 'The pipeline below is illustrative'],
  },
  {
    to: '/',
    tab: 'Dashboard',
    nav: '/',
    title: 'Dashboard — what needs you today',
    body: 'Your landing page, and deliberately not a metrics wall. The hero is one ranked action queue: everything with a deadline, soonest first, with the reason written in the row. An option period ending today outranks a closing next week. Underneath it: your production against a goal you set, and your earnest money split into sent, pending and lost.',
    live: ['Deadlines, countdowns and the ranked queue', 'Goal pacing against the calendar'],
    staged: ['The deals being counted are seeded for this demo'],
  },
  {
    to: '/mls-opportunities',
    tab: 'MLS Opportunities',
    nav: '/mls-opportunities',
    title: 'MLS Opportunities — where deals come from',
    body: 'A live feed of the Phoenix market, already filtered. Every listing is scored in two passes: deterministic rules first (age, keywords, retail markers), then Claude reads the photos and rejects anything that looks turnkey. What survives is graded Hot / Watch / Maybe. Open one for ARV, a live rehab estimate and MAO scenarios, and use Quick Search to pull any address or MLS number on demand.',
    live: ['Real ARMLS listings, photos, and sold comps', 'Real AI photo scoring and opportunity grades', 'Quick Search against the live feed'],
    staged: ['Scheduled market re-scans are switched off for this workspace'],
  },
  {
    to: '/off-market',
    tab: 'Off Market',
    nav: '/off-market',
    title: 'Off Market — your own leads',
    body: 'Not every deal comes from the MLS. Drop in a lead by hand or paste a list of addresses, and it lands in the same workflow — underwrite it, generate a contract from it, match buyers to it. Duplicates are caught by address, so importing the same list twice does not give you the same lead twice.',
    live: ['Import, dedupe, and the full contract flow'],
    staged: [],
  },
  {
    to: '/deals',
    tab: 'Deals',
    nav: '/deals',
    title: 'Deals — your working pipeline',
    body: 'Everything you personally have going, by stage: Available → Tagged → Offer Sent → Under Contract → Sold → Closed. Open any deal for the AI underwrite with its comp set and map, the rehab breakdown, generated contracts, notes and the full email thread. This is where most of the day actually happens.',
    live: ['AI underwriting and the comp engine', 'TREC contract generation from the official template'],
    staged: ['These deals were seeded onto real Phoenix listings so the analysis is genuine'],
  },
  {
    to: '/pipeline',
    tab: 'Pipeline',
    nav: '/pipeline',
    title: 'Pipeline — the whole office, and the money',
    body: 'The org-wide view. Active inventory, units closed, and fee volume, with a period toggle and drill-down tables behind every number. Fees are commission plus assignment fee — commission is a percentage of the contract price, the assignment fee is the spread between what you tie it up at and what you sell it for. Financial fields are redacted server-side for anyone who should not see them.',
    live: ['The redaction rules — a sales agent never receives the contract price'],
    staged: ['Closings and fees shown are from the seeded pipeline'],
  },
  {
    to: '/buyers',
    tab: 'Buyers Database',
    nav: '/buyers',
    title: 'Buyers Database — the demand side',
    body: 'Your investors and their buy-boxes: areas, price ceiling, minimum beds and sqft, strategy. feebe scores every buyer against a deal on location, quality, strategy and price, and ranks them, so marketing a property starts from a list rather than a guess. Buyers are private to the agent who owns them by default; an org can switch to a shared book.',
    live: ['Buyer match scoring and ranking', 'Per-agent visibility rules'],
    staged: ['These buyers are fictional — no real investor contact details are in this workspace'],
  },
  {
    to: '/communications',
    tab: 'Communications',
    nav: '/communications',
    title: 'Communications — every message, one thread',
    body: 'Email and SMS across every deal, inbound and outbound, with opens and replies. Agents send from their own Gmail so replies land where they expect, and feebe syncs the thread back. Follow-ups are reminders, never automatic sends — nothing leaves feebe without a person pressing Send.',
    live: ['Threading, logging, and reply tracking'],
    staged: ['Every send from this workspace is logged and shown, but nothing is delivered — messages appear here marked "simulated"'],
  },
  {
    to: '/settings',
    tab: 'Settings',
    nav: '/settings',
    title: 'Settings — make it yours',
    body: 'Upload your own purchase contract and map its fields once; feebe fills it per deal from then on. Same for offer documents like a proof of funds or an agency disclosure — the mappable ones are filled with that deal\'s address and dates at the moment you send. Also here: email templates, your Gmail connection, workspace defaults and the market timezone every countdown is computed in.',
    live: ['Template upload, field mapping and document generation'],
    staged: ['Connecting a live mailbox is available, but sends stay simulated in this workspace'],
  },
  {
    to: '/',
    tab: 'That\'s the tour',
    nav: null,
    title: 'That\'s the whole product',
    body: 'Have a dig around — nothing here can send a message, charge anything, or reach a real agent or investor, so there is nothing you can break. This workspace is fully isolated: its data is invisible to every other account on feebe, and every other account\'s data is invisible to it.',
    live: ['Everything you see about the market is real'],
    staged: ['Everything that would leave the building is simulated'],
    final: true,
  },
];

const RESUME_KEY = 'feebe.tour.step';

export default function Walkthrough() {
  const { user, org, plan, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const guided = !!org?.simulated_outbound && plan?.key !== 'demo';
  const active = !!user && !user.onboarded && !dismissed;

  // Resume where they left off if the tab was reloaded mid-tour. Wrapped because storage throws in
  // some privacy modes, and a tour is not worth a white screen.
  useEffect(() => {
    if (!guided || !active) return;
    try {
      const saved = parseInt(window.localStorage.getItem(RESUME_KEY), 10);
      if (Number.isInteger(saved) && saved > 0 && saved < TOUR_STEPS.length) setStep(saved);
    } catch { /* no resume */ }
  }, [guided, active]);

  // Drive the router from the step. Runs on mount too, which is what puts the user on the Dashboard
  // for step 1 regardless of where they happened to land.
  useEffect(() => {
    if (!guided || !active) return;
    const dest = TOUR_STEPS[step]?.to;
    if (dest) navigate(dest);
    try { window.localStorage.setItem(RESUME_KEY, String(step)); } catch { /* best-effort */ }
  }, [step, guided, active, navigate]);

  if (!active) return null;

  const finish = async (navTo) => {
    setDismissed(true);                         // hide immediately for this session
    try { window.localStorage.removeItem(RESUME_KEY); } catch { /* best-effort */ }
    try { await apiSend('/api/auth/onboarded', { method: 'POST' }); } catch { /* best-effort */ }
    refresh();                                  // re-pull /me so onboarded=true persists
    if (navTo) navigate(navTo);
  };

  if (guided) {
    return <GuidedTour step={step} setStep={setStep} finish={finish} />;
  }

  const steps = plan?.key === 'demo' ? DEMO_STEPS : REAL_STEPS;
  return <ModalTour steps={steps} step={step} setStep={setStep} finish={finish} />;
}

// Spotlight the sidebar entry the current step is about.
//
// Done as an injected stylesheet targeting `nav a[href="…"]` rather than by threading state down
// into Layout/Sidebar. The NavLinks already render their `to` as a plain href, so this needs no
// prop, no context and no change to the nav's own code — and when the tour is not running there is
// no stylesheet and therefore nothing to leave behind.
//
// The active-route styling alone is not enough. React Router already tints the current tab, so by
// the time a step's text is read the nav looks exactly as it always does; the ring and its pulse are
// what connect the words to the place in the UI they are describing. Steps that are not about a tab
// (the intro and the wrap-up) carry nav: null and spotlight nothing, because pointing at Dashboard
// while talking about the product as a whole would be a lie about where to look.
function NavSpotlight({ href }) {
  if (!href) return null;
  return (
    <style>{`
      @keyframes feebeTourPulse {
        0%, 100% { box-shadow: 0 0 0 2px #14504A, 0 0 0 5px rgba(20,80,74,0.22); }
        50%      { box-shadow: 0 0 0 2px #14504A, 0 0 0 10px rgba(20,80,74,0); }
      }
      nav a[href="${href}"] {
        border-radius: 8px;
        animation: feebeTourPulse 1.9s ease-in-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        nav a[href="${href}"] {
          animation: none;
          box-shadow: 0 0 0 2px #14504A, 0 0 0 5px rgba(20,80,74,0.22);
        }
      }
    `}</style>
  );
}

// ── Docked tour panel ─────────────────────────────────────────────────────────
function GuidedTour({ step, setStep, finish }) {
  const [collapsed, setCollapsed] = useState(false);
  const cur = TOUR_STEPS[step];
  const last = step === TOUR_STEPS.length - 1;
  if (!cur) return null;

  // Collapsed: a pill that says where you are and offers the way back in. Deliberately not a bare
  // icon — someone who parked the tour to look at something needs to see, at a glance, that it is
  // still there and how far through it they were.
  if (collapsed) {
    return (
      <>
      <NavSpotlight href={cur.nav} />
      <button
        onClick={() => setCollapsed(false)}
        style={{
          position: 'fixed', right: 22, bottom: 22, zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '10px 15px', borderRadius: 999,
          background: 'var(--teal)', color: '#06121A', border: 'none',
          fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        }}>
        <span>Resume tour</span>
        <span style={{ opacity: 0.7, fontWeight: 600 }}>{step + 1}/{TOUR_STEPS.length}</span>
      </button>
      </>
    );
  }

  return (
    <>
    <NavSpotlight href={cur.nav} />
    <div style={{
      position: 'fixed', right: 22, bottom: 22, zIndex: 1000,
      width: 392, maxWidth: 'calc(100vw - 44px)', maxHeight: 'calc(100vh - 44px)',
      overflowY: 'auto',
    }}>
      <div style={{
        background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
        borderRadius: 14, padding: '16px 18px 15px',
        boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
      }}>
        {/* header: tab name + progress + skip */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase',
              color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: 5,
              padding: '3px 7px', whiteSpace: 'nowrap',
            }}>{cur.tab}</span>
            <span style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
              Step {step + 1} of {TOUR_STEPS.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
            {/* Hide ≠ Skip. Hiding parks the tour so the page underneath can be inspected and keeps
                the step; skipping ends it for good. Two words, because conflating them means the
                only way to see behind the card is to lose your place. */}
            <button onClick={() => setCollapsed(true)} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 12, cursor: 'pointer' }}>
              Hide
            </button>
            <span style={{ color: 'var(--navy-border)', fontSize: 12 }}>|</span>
            <button onClick={() => finish()} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 12, cursor: 'pointer' }}>
              Skip tour
            </button>
          </div>
        </div>

        {/* progress rail */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
          {TOUR_STEPS.map((_, i) => (
            <span key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= step ? 'var(--teal)' : 'var(--navy-border)',
              transition: 'background .2s',
            }} />
          ))}
        </div>

        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>{cur.title}</div>
        <div style={{ fontSize: 13.5, color: 'var(--gray-300)', lineHeight: 1.6, marginBottom: 14 }}>{cur.body}</div>

        {(cur.live?.length || cur.staged?.length) ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {cur.live?.length ? <Legend tone="live" label="Real &amp; live" items={cur.live} /> : null}
            {cur.staged?.length ? <Legend tone="staged" label="Simulated" items={cur.staged} /> : null}
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid var(--navy-border)',
              background: 'transparent', color: step === 0 ? 'var(--navy-border)' : 'var(--gray-300)',
              fontSize: 13, cursor: step === 0 ? 'default' : 'pointer',
            }}>
            Back
          </button>
          <button
            onClick={() => (last ? finish('/') : setStep((s) => s + 1))}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: 'var(--teal)', color: '#06121A', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}>
            {last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

function Legend({ tone, label, items }) {
  const color = tone === 'live' ? '#4ADE80' : '#FBBF24';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: 4, background: color }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color }}>{label}</span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 15, color: 'var(--gray-400, var(--gray-500))', fontSize: 12, lineHeight: 1.6 }}>
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

// ── Original modal (demo plan + brand-new real accounts), unchanged ────────────
function ModalTour({ steps, step, setStep, finish }) {
  const cur = steps[step];
  const last = step === steps.length - 1;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(8,12,20,0.72)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {steps.map((_, i) => (
              <span key={i} style={{ width: i === step ? 18 : 7, height: 7, borderRadius: 4, background: i === step ? 'var(--teal)' : 'var(--navy-border)', transition: 'all .2s' }} />
            ))}
          </div>
          <button onClick={() => finish()} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 12, cursor: 'pointer' }}>Skip</button>
        </div>

        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--white)', marginBottom: 10 }}>{cur.title}</div>
        <div style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.55, marginBottom: 24 }}>{cur.body}</div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'transparent', color: step === 0 ? 'var(--navy-border)' : 'var(--gray-300)', fontSize: 13, cursor: step === 0 ? 'default' : 'pointer' }}>
            Back
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            {cur.cta && (
              <button onClick={() => finish(cur.cta.to)}
                style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {cur.cta.label}
              </button>
            )}
            <button
              onClick={() => last ? finish() : setStep(s => s + 1)}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#06121A', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {last ? 'Get started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
